/**
 * S7 (tier 2's rocket tube; decision 3, direct fire first): a rocket's burst — a person-family blast under SCALE.md §6:
 * - **reach:** its own person-scale radius (the weapon's, × k), never a shell's;
 * - **damage:** the weapon's number, falling off to nothing at the edge of the damage circle (core/damage.ts);
 * - **push:** converted into the person family (× k), so a person flies the body lengths a classic unit would;
 * - **cover:** a person behind what stops a bullet (the props, the ground: `segmentCovered`) is spared;
 * - **no crater:** a person-scale explosion never edits the coarse mesh or a planet's mass (a scorch, presentation only).
 * The rest is the shared blast — structures, hazards, flags and drop cans in reach take it as from a shell — and the
 * trees in its own radius take it as a burst (63.4 step 6: three rockets fell one). Pure.
 */

import { explosionPush, explosionSuppress, blastOwnerTeam, burstModelFor } from './explosions';
import type { ExplosionClass } from '../constants/explosions';
import type { GameState, Vec3 } from '../types';
import { vecAdd, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { calculateDamage, creditHit, friendlyShare } from './damage';
import { installationsOn, shieldFactorFor } from './installations';
import { hitboxCenter, segmentCovered } from './bullets';
import { personSetOf } from './scaleProfile';
import { blastProps } from './propDamage';
import { structureShapeReach } from './blastReach';
import { clockPolicyFor } from '../match/clockPolicy';
import { shipBlastPass } from './shipDamage';
import { shedStructureParts } from './partDebris';
import { zonesOn } from './vehicleZones';
import { BURST_PENETRATION } from '../constants/vehicleZones';
import { toughShareFor } from './charges';

export interface RocketBlastResult {
  state: GameState;
  damages: Array<{ playerId: number; damage: number }>;
  kills: number[];
  launched: number[];
  /** 63.4 step 6: the trees it felled */
  felled: number[];
}

/** A rocket of `ownerId` bursts at `point` (on planet `planetIndex`, or in the air at -1) with the weapon's `blast`. */
export function rocketBlast(state: GameState, ownerId: number, point: Vec3, planetIndex: number, blast: { radius: number; damage: number }, cls?: ExplosionClass): RocketBlastResult {
  const ps = personSetOf(state);
  const shooter = state.players.find(p => p.id === ownerId);
  // the cover test starts a hair off what the rocket hit (outside the ground or the prop it struck)
  const planet = state.planets[planetIndex];
  const centre = planet ? vecAdd(point, vecScale(vecNormalize(vecSub(point, planet.position)), 0.25 * ps.unitRadius)) : point;
  const push = explosionPush(state, cls);   // §5.21 step 6: its class's push, with Bursts throw on
  const model = burstModelFor(state, cls, blast);   // 69.65: the burst as one model (two radii, hulls by kind)
  const reach = Math.max(blast.radius * 3, push?.radius ?? 0, model ? model.wound + model.body.stand : 0);   // the push's circle, the widest of the blast's (core/damage.ts)
  const covered = new Set<number>();
  for (const p of state.players) {
    if (!p.alive || p.airborne) continue;
    const body = hitboxCenter(p, state.planets, ps.hitboxCenter, ps);
    if (vecLength(vecSub(body, centre)) > reach) continue;
    if (segmentCovered(centre, body, state, undefined, ps.coverSampleStep)) covered.add(p.id);
  }
  // (the shared blast reads a weapon definition for everything but damage and radius: the standard shell's, which has no
  // specials). The covered are spared, not left out: a structure's side is still its owner's (130 (d)). 63.4 step 7: under
  // the clock policy's `blastReach: 'shape'` a structure is reached by its shape, and spared behind what stops a bullet
  const dmg = calculateDamage(
    state.players, state.planets, point, 'standard', state.structures, shooter?.teamIndex, friendlyShare(state.config),
    state.hazards, state.config.knockbackMultiplier ?? 1, ownerId, state.plantedFlags, state.crashedDropCans,
    { damage: blast.damage, blastRadius: blast.radius }, ps.k,
    clockPolicyFor(state.config).blastReach === 'shape' ? structureShapeReach(state, point, 'person', centre, reach) : undefined,   // (300's cost: no cover test past the widest circle)
    installationsOn(state) ? p => shieldFactorFor(state, p) : undefined,   // X18 step 2
    'burst',
    covered,
    push,
    cls && zonesOn(state.config) ? { pen: BURST_PENETRATION[cls], at: point, state } : undefined,   // 63.12: the class's penetration against the vehicles it meets
    model ?? undefined,
    toughShareFor(state, cls, shooter !== undefined),   // 69.47 step 1: with the charges, a toughened story target's share of a squad's burst (a person's, not a ship's)
  );
  const total = dmg.damages.reduce((sum, d) => sum + d.damage, 0);
  const credited = dmg.players.map(q => q.id === ownerId ? creditHit(q, total, dmg.kills.length) : q);
  // §5.21 step 7: with Bursts suppress (and Suppression) on, the owner's enemies within its class's reach are marked — as a near
  // round marks, renewed and never shortened; through cover too (a burst is heard and felt)
  const sup = explosionSuppress(state, cls), team = sup ? blastOwnerTeam(state, ownerId) : undefined;
  const players = !sup ? credited : credited.map(q => q.alive && q.planetIndex === planetIndex && q.teamIndex !== team
    && vecLength(vecSub(hitboxCenter(q, state.planets, ps.hitboxCenter, ps), point)) <= sup.radius ? { ...q, suppressed: Math.max(q.suppressed ?? 0, sup.seconds) } : q);
  // 63.4 step 6 (130 (a)): the trees within the burst's own radius take it (a rocket takes three to fell one); rocks stand
  const props = blastProps(
    { ...state, players, structures: dmg.structures, hazards: dmg.hazards, plantedFlags: dmg.plantedFlags, crashedDropCans: dmg.crashedDropCans },
    planetIndex, point, blast.radius, blast.damage, 'burst', ownerId,
  );
  return {
    // partial destruction step 5: a detaching part at 0 falls away; 69.3b: the ships within the burst's reach wear it (nothing off)
    state: shipBlastPass(shedStructureParts(state.structures, props.state, point, 'burst', ownerId), planetIndex, point, blast.radius, blast.damage, 'burst'),
    damages: dmg.damages, kills: dmg.kills, launched: dmg.knockbacks.filter(k => k.launched).map(k => k.playerId), felled: props.felled,
  };
}
